import { type ICustomImage } from "@filmweb/sanity-types";
import type { ImageUrlBuilder } from "@sanity/image-url";
import { HIGH_DENSITY_QUALITY, MAX_1X_VIEWPORT, SRCSET_WIDTHS } from 'src/components/images/imageSizes';
import { urlForAsset, withImageFormat, withShareImageFormat } from 'src/lib/client/imageUrls';
import type { ImageHotspot } from 'sanity';

const POSTER_ASPECT = process.env.NEXT_PUBLIC_WEBSITE_BRAND === "kinodk" ? 800 / 1200 : 271 / 384;

const imageSizeRegExp = /^image-.*?-(\d+)x(\d+)-.*$/i;
const imageSizeUrlRegExp = /-(\d+)x(\d+)\./i;
// 9-1920x1080.jpg?auto=format"
type Size = {
    width: number,
    height: number,
    aspect: number
};

export function getImageUrlForWidth(image: ICustomImage, width: number): string {
    const assetRef = image.asset!._id ?? image.asset!._ref;
    const size = getOrgSize(assetRef);
    const w = Math.min(width, size.width);
    const h = Math.round(w / size.aspect);
    let imgBuilder = withShareImageFormat(urlForAsset(assetRef), assetRef).width(w).height(h);
    if (image.hotspot) {
        imgBuilder = doCrop(imgBuilder, image.asset?.hotspot);
    }
    return imgBuilder.url();
}

export function getOrgSizeForImageUrl(imgUrl?: string): Size | null {
    if( !imgUrl ) return null;
    const res = imageSizeUrlRegExp.exec(imgUrl);
    const w = parseInt(res![1]);
    const h = parseInt(res![2]);
    return { width: w, height: h, aspect: w / h };
}

export function getOrgSize(assetRef: string): Size {

    const res = imageSizeRegExp.exec(assetRef);
    const w = parseInt(res![1]);
    const h = parseInt(res![2]);
    return { width: w, height: h, aspect: w / h };
}

export function doCrop(imageBuilder: ImageUrlBuilder, hotspot?: ImageHotspot): ImageUrlBuilder {
    let fpx = 0.5;
    let fpy = 0.5;
    if (hotspot) {
        fpx = hotspot.x;
        fpy = hotspot.y;
    }
    return imageBuilder.fit("crop").crop("focalpoint").focalPoint(fpx, fpy);
}

export type CmsImageAttrsOptions = {
    assetRef: string;
    width?: number | string;
    height?: number | string;
    sizes?: string;
    unoptimized?: boolean;
    crop?: boolean;
    isPosterAspect?: boolean;
    nonPosterAspect?: number | string;
    hotspot?: ImageHotspot;
    /** No HIGH_DENSITY_QUALITY: for the page's main image, where the lower quality shows */
    fullQuality?: boolean;
    /** Url builder for the Sanity project the image is in. Defaults to the brand's own project. */
    urlFor?: (assetRef: string) => ImageUrlBuilder;
};

export type CmsImageAttrs = {
    src: string;
    srcSet?: string;
    sizes?: string;
    width: number;
    height: number;
};

/**
 * src, srcset, sizes and dimensions for a Sanity image:
 * - unoptimized: a single cropped url for the given size, no srcset (as next/image rendered it)
 * - with sizes: every width in SRCSET_WIDTHS with w descriptors, so the browser picks from the displayed size
 * - without sizes: exactly 1x and 2x of the width
 * No width goes beyond the original's: Sanity scales images up past it, which only adds bytes.
 * Widths only 2x+ screens pick (2x, or above what a 1x screen picks for the largest slot in sizes) get HIGH_DENSITY_QUALITY,
 * unless fullQuality is set.
 */
export function getCmsImageAttrs({ assetRef, width, height, sizes, unoptimized = false, crop = false, isPosterAspect = false, nonPosterAspect, hotspot, fullQuality = false, urlFor = urlForAsset }: CmsImageAttrsOptions): CmsImageAttrs {
    const size = getOrgSize(assetRef);
    const customAspect = toAspect(nonPosterAspect);
    const aspect = !isPosterAspect && customAspect ? customAspect : size.aspect;
    let w = Number(width ?? size.width);
    let h = Math.round(Number(height ?? w / aspect));
    if (!width && height) {
        w = Math.round(h * aspect);
    }

    if (unoptimized) {
        if (isPosterAspect) {
            h = Math.floor(w / POSTER_ASPECT);
        }
        const src = doCrop(withImageFormat(urlFor(assetRef), assetRef).width(w).height(h), hotspot).url();
        return { src, srcSet: undefined, sizes: undefined, width: w, height: h };
    }

    const cropAspect = isPosterAspect ? POSTER_ASPECT : aspect;
    const { widths, kind } = getSrcSetWidths(size.width, w, sizes);
    const largest1x = kind === "x" ? widths[0] : (widths.find(srcWidth => srcWidth >= getMaxSlotWidth(sizes!)) ?? widths[widths.length - 1]);
    const urlForWidth = (srcWidth: number) => {
        let builder = urlFor(assetRef);
        if (crop) {
            builder = doCrop(builder, hotspot);
        }
        builder = builder.width(srcWidth).height(Math.floor(srcWidth / cropAspect));
        if (!fullQuality && srcWidth > largest1x) {
            builder = builder.quality(HIGH_DENSITY_QUALITY);
        }
        return withImageFormat(builder, assetRef).url();
    };
    return {
        sizes,
        srcSet: widths.map((srcWidth, i) => `${urlForWidth(srcWidth)} ${kind === "w" ? srcWidth : i + 1}${kind}`).join(", "),
        src: urlForWidth(widths[widths.length - 1]),
        width: w,
        height: h,
    };
}

/**
 * The srcset widths for an image whose original is orgWidth wide: with sizes, SRCSET_WIDTHS (by w);
 * without, exactly the width and twice the width (by x). Widths beyond the original are replaced by its width.
 */
export function getSrcSetWidths(orgWidth: number, width: number, sizes?: string): { widths: number[], kind: "w" | "x" } {
    const capped = (widths: readonly number[]) => [...new Set(widths.map(srcWidth => Math.min(srcWidth, orgWidth)))];
    if (sizes) {
        return { widths: capped(SRCSET_WIDTHS), kind: "w" };
    }
    return { widths: capped([width, width * 2]), kind: "x" };
}

type SizesEntry = { minWidth: number, maxWidth: number, isUnknown: boolean, px?: number, vw?: number };

/**
 * The largest slot, in CSS px, sizes gives on screens up to MAX_1X_VIEWPORT wide. Infinity when it can't tell.
 * It understands min-width and max-width conditions joined by "and", and px or vw values. An orientation condition
 * can't be told from the width, so that entry counts as possible and the entries after it too.
 */
export function getMaxSlotWidth(sizes: string): number {
    const entries: SizesEntry[] = [];
    for (const part of splitTopLevel(sizes)) {
        const match = /^(.*?)\s*(\d+(?:\.\d+)?)(px|vw)$/.exec(part);
        if (!match) {
            return Infinity;
        }
        const entry: SizesEntry = { minWidth: 0, maxWidth: Infinity, isUnknown: false, [match[3]]: parseFloat(match[2]) };
        const condition = match[1].replace(/[()]/g, " ").trim();
        for (const feature of condition ? condition.split(/\s+and\s+/) : []) {
            const f = /^(min-width|max-width|orientation)\s*:\s*(\d+(?:\.\d+)?px|landscape|portrait)$/.exec(feature.trim());
            if (!f) {
                return Infinity;
            }
            if (f[1] === "orientation") {
                entry.isUnknown = true;
            } else if (f[1] === "min-width") {
                entry.minWidth = Math.max(entry.minWidth, parseFloat(f[2]));
            } else {
                entry.maxWidth = Math.min(entry.maxWidth, parseFloat(f[2]));
            }
        }
        entries.push(entry);
    }
    // Slots grow with the viewport between breakpoints, so the largest is at a breakpoint or at the widest screen
    const viewports = [MAX_1X_VIEWPORT, ...entries.flatMap(e => [e.minWidth - 1, e.minWidth, e.maxWidth, e.maxWidth + 1])]
        .filter(v => v > 0 && v <= MAX_1X_VIEWPORT);
    return Math.max(...viewports.flatMap(viewport => slotsAt(entries, viewport)));
}

/** The slots sizes can give on a viewport: the first matching entry, or every entry up to it when some can't be told */
function slotsAt(entries: SizesEntry[], viewport: number): number[] {
    const slots: number[] = [];
    for (const entry of entries) {
        if (viewport < entry.minWidth || viewport > entry.maxWidth) {
            continue;
        }
        slots.push(entry.px ?? Math.floor(entry.vw! * viewport / 100));
        if (!entry.isUnknown) {
            return slots;
        }
    }
    // No matching entry means 100vw
    return [...slots, viewport];
}

/** Splits sizes at the commas between entries, not those inside parentheses */
function splitTopLevel(sizes: string): string[] {
    const parts: string[] = [];
    let depth = 0, current = "";
    for (const ch of sizes) {
        if (ch === "(") depth++;
        if (ch === ")") depth--;
        if (ch === "," && depth === 0) {
            parts.push(current.trim());
            current = "";
        } else {
            current += ch;
        }
    }
    parts.push(current.trim());
    return parts.filter(Boolean);
}

function toAspect(aspect?: number | string): number | undefined {
    if (typeof aspect === "string") {
        const [num, den] = aspect.split("/").map(Number);
        return num / den;
    }
    return aspect;
}
